import React, {
  useEffect, useRef, useState, useReducer,
} from 'react';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import {
  getBrandingColor,
  colorTypes,
  styleOverrides,
  getBrandingStyleOverride,
} from 'src/branding';
import { usePageWidth } from 'src/utils/hooks';
import { inRange } from 'src/utils/misc.utils';
import { ActionButton } from '../routes/Statistics/Components';
import { StyledTable } from './Table';

const borderHeight = 1;

const mobileBreakpoint = `${400}px`;
const medimBreakpoint = 800;

const borderColor = getBrandingColor(colorTypes.statisticsTableBorder);



const DiseaseNameHeaderWrapper = styled.div`
  @media screen and (max-width: ${mobileBreakpoint}) {
    span[name="partOne"],
    span[name="partTwo"] {
      display: block;
    }
  }
`;

export const DiseaseNameHeader = () => (
  <DiseaseNameHeaderWrapper>
    <span name="partOne">Tekst</span>
    <span name="partTwo">(anmerkning)</span>
  </DiseaseNameHeaderWrapper>
);

const DiseaseStatisticsTableWrapper = styled.div`
  position: relative;

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    /* IE10+ specific styles go here */
    width: 700px;
    ${({ IEWrapperCSS }) => (IEWrapperCSS ? IEWrapperCSS : '')};
  }
`;

const HorizontalScrollIndicator = styled(motion.div)`
    position: absolute;
    width: 30px;
    height:100%;
    background: linear-gradient(90deg, transparent, rgba(0,0,0,0.3));
    z-index: 2;
    right: 0;
    bottom: 0;
    transition: opacity 0.2s;
`;

const VerticalScrollbarIndicator = () => (
  <div style={{
    background: 'linear-gradient(0deg, rgba(0,0,0,0.3), transparent)',
    width: '100%',
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: '72px',
  }}
  />
);

const ExpandCollapseButton = ({ isCollapsed, children, ...props }) => (
  <ActionButton
    {...props}
    style={{
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      overflow: 'visible',
      width: '21.4%',
      fontSize: '0.87em',
      color: '#4a4a4a',
      borderColor: '#868686',
      bottom: isCollapsed ? '0.6em' : '-4.4em',
    }}
    isCollapsed={isCollapsed}
  >
    {children}
  </ActionButton>
);

const DiseaseStatisticsTable = ({
  data,
  headers,
  subHeaders = [],
  headerElem,
  wrapperStyle,
  IEWrapperCSS,
  noDataStr,
  expandCollapseButtonEnabled,
  key,
}) => {
  const [scrollLeftPos, setScrollLeftPos] = useState(0);
  const [scrollTopPos, setScrollTopPos] = useState(0);
  const [heightTopHeader, setHeightTopHeader] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(expandCollapseButtonEnabled);
  const [hasScroll, setHasScroll] = useState(true);
  const wrapperRef = useRef(null);
  const pageWidth = usePageWidth();
  const tableContentWrapper = useRef(null);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const showExpandCollapseButton = expandCollapseButtonEnabled && hasScroll;

  const TableWrap = styled.div`
  `;

  const onScroll = (evt) => {
    const scrollLeft = evt.target.scrollLeft;
    const scrollRight = evt.target.scrollRight;

    if (scrollLeft !== scrollLeftPos) {
      setScrollLeftPos(evt.target.scrollLeft);
    }
    if (scrollRight !== scrollTopPos) {
      setScrollTopPos(evt.target.scrollTop);
    }
    setTopHeaderHeight();
  };

  const setTopHeaderHeight = () => {
    if (data.length === 0) {
      return;
    }
    const sampleCell = document.querySelector(
      '#statisticsTable thead th:nth-child(2)',
    );
    setHeightTopHeader(sampleCell.clientHeight + borderHeight);
  };

  const onResizePage = () => {
    if (!wrapperRef?.current || data.length === 0) {
      return;
    }
    const scrollLeft = wrapperRef.current.scrollLeft;
    const scrollRight = wrapperRef.current.scrollRight;

    if (scrollLeft !== scrollLeftPos) {
      setScrollLeftPos(scrollLeft);
    }
    if (scrollRight !== scrollTopPos) {
      setScrollTopPos(scrollRight);
    }
    setTopHeaderHeight();
  };

  useEffect(() => {
    setTopHeaderHeight();
    window.addEventListener('resize', onResizePage);

    return () => {
      window.removeEventListener('resize', onResizePage);
    };
  }, []);

  useEffect(() => {
    const elem = document.querySelector('#diseaseTableWrapper');

    setHasScroll(elem.scrollHeight > elem.offsetHeight);
  }, [key]);

  const formatCellValue = (val, customDisplayValue) => customDisplayValue || customDisplayValue === '' || val;

  const onScrollTableContent = () => {
    forceUpdate();
  };

  useEffect(() => {
    if (tableContentWrapper.current) {
      tableContentWrapper.current.addEventListener('scroll', onScrollTableContent);
    }
    const currRef = tableContentWrapper.current;
    return () => currRef && currRef.removeEventListener('scroll', onScrollTableContent);
  }, [tableContentWrapper]);

  const tableInnerScrolledAllToRight = tableContentWrapper.current && inRange(tableContentWrapper.current.scrollWidth - tableContentWrapper.current.scrollLeft + 1, [tableContentWrapper.current.clientWidth - 1, tableContentWrapper.current.clientWidth + 1]);
  const tablaInnerHasScrollbar = tableContentWrapper.current && tableContentWrapper.current.scrollWidth > tableContentWrapper.current.clientWidth;

  return (
    <DiseaseStatisticsTableWrapper
      id="diseaseTableWrapper"
      isCollapsed={isCollapsed}
      style={
        isCollapsed ? {
          ...wrapperStyle,
          maxHeight: pageWidth < 400 ? '250px' : '263px',
          overflow: 'hidden',
        } : {
          ...wrapperStyle,
          maxHeight: 'initial',
          overflow: 'visible',
          transition: { duration: 0.3 },
        }
}
      useCollapse={showExpandCollapseButton}
      IEWrapperCSS={IEWrapperCSS}
      onScroll={onScroll}
      ref={(ref) => wrapperRef}
    >
      {data.length === 0 ? (
        <p style={{ fontStyle: 'italic' }}>{noDataStr}</p>
      ) : (
        <>
          <div
            style={{ overflow: 'auto', position: 'relative' }}
            ref={tableContentWrapper}
            id="statisticsTableContentWrapper"
          >

            <StyledTable
              hasScrolledTop={scrollTopPos > 0}
              id="statisticsTable"
              heightTopHeader={heightTopHeader}
            >
              <colgroup>
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead>
                {/* Headers */}
                <tr>
                  <th rowSpan={2} name="mainHeader">
                    {headerElem}
                  </th>
                  {headers.map((header, i) => (
                    <th name="header" {...header.itemProps} key={i}>
                      {header.value}
                    </th>
                  ))}
                </tr>
                {/* SubHeaders */}
                <tr>
                  {subHeaders.map((subHeader, i) => (
                    <>
                      <th name="subHeader" {...subHeader.itemProps}>
                        {subHeader.value}
                      </th>
                    </>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Disease data */}
                {data.map((row) => (
                  <tr {...row.itemProps}>
                    {row.diseaseData.map((cell, i) => (i === 0 ? (
                      <th key={i} name="diseaseName" {...cell.itemProps}>
                        <TableWrap>{formatCellValue(cell.value, cell.customDisplayValue)}</TableWrap>
                      </th>
                    ) : (
                      <td key={i} {...cell.itemProps}>
                        <TableWrap>{formatCellValue(cell.value, cell.customDisplayValue)}</TableWrap>
                      </td>
                    )))}
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </div>

          {tablaInnerHasScrollbar
            && (
              <HorizontalScrollIndicator
                variants={{
                  visible: {
                    opacity: 1, transition: { duration: 0.15 },
                  },
                  hidden: {
                    opacity: 0, transition: { duration: 0.15 },
                  },
                }}
                animate={
              tableInnerScrolledAllToRight ? 'hidden' : 'visible'
            }
              />
            )}
          {showExpandCollapseButton
          && (
            <>
              {isCollapsed && pageWidth > medimBreakpoint
                && <VerticalScrollbarIndicator />}
              <ExpandCollapseButton
                onClick={() => setIsCollapsed((old) => !old)}
                isCollapsed={isCollapsed}
              >
                {isCollapsed ? 'Vis mer' : 'Lukk'}
              </ExpandCollapseButton>
            </>
          )}
        </>
      )}
    </DiseaseStatisticsTableWrapper>
  );
};

export default DiseaseStatisticsTable;
